/*
 * Copyright (c) 2026, Wultra s.r.o. (www.wultra.com).
 *
 * All rights reserved. This source code can be used only for purposes specified
 * by the given license contract signed by the rightful deputy of Wultra s.r.o.
 * This source code can be used only by the owner of the license.
 *
 * Any disputes arising in respect of this agreement (license) shall be brought
 * before the Municipal Court of Prague.
 */

import { observer } from "mobx-react-lite"
import type { To } from "react-router"
import { Link } from "react-router"

import type { UserList } from "../../features/user/user-types"
import { formatRole } from "../../features/user/user-types"
import { valueOrDash } from "../../utils/value-or-dash"
import { Truncate } from "../components/truncate"
import { Badge } from "../primitives/badge"
import { TableCell, TableRow } from "../primitives/table"

import styles from "./user-row.module.css"

/**
 * Badge classes for a user's status.
 *
 * Disabled uses the semantic destructive token; the theme has no green token,
 * so enabled uses Tailwind's palette, tinted like the threat-level badges.
 */
const StatusColor = {
    Enabled: "bg-green-500/10 text-green-700",
    Disabled: "bg-destructive/10 text-destructive"
} as const

const StatusBadge = ({ enabled }: { enabled: boolean }) => {
    const status = enabled ? "Enabled" : "Disabled"

    return <Badge className={StatusColor[status]}>{status}</Badge>
}

/**
 * Badge classes for a user's type.
 *
 * Internal uses the brand primary token; the theme has no orange token, so
 * external uses Tailwind's palette, tinted like the threat-level badges.
 */
const TypeColor = {
    Internal: "bg-primary/10 text-primary",
    External: "bg-orange-500/10 text-orange-700"
} as const

const TypeBadge = ({ external }: { external: boolean }) => {
    const type = external ? "External" : "Internal"

    return <Badge className={TypeColor[type]}>{type}</Badge>
}

interface Props {
    /**
     * User to display. A missing name shows an em dash.
     */
    user: UserList
    /**
     * User detail the row opens.
     */
    to: To
}

/**
 * Renders a user as a table row with name, email, status, type and roles.
 *
 * The email is a real link, and the row's CSS module stretches it over the
 * whole row. Clicking anywhere opens the user detail, a middle or Ctrl/Cmd
 * click opens it in a new tab, and the row stays a single tab stop.
 */
export const UserRow = observer(({ user, to }: Props) => (
    <TableRow className={styles.row}>
        <TableCell>{valueOrDash(user.name)}</TableCell>
        {/* `max-w-0` drops the email's intrinsic width, so the column takes the spare space and truncates. */}
        <TableCell className="max-w-0">
            <Link to={to} className={styles.link}>
                <Truncate className="relative z-1 block" text={valueOrDash(user.username)} />
            </Link>
        </TableCell>
        <TableCell>{user.enabled !== undefined && <StatusBadge enabled={user.enabled} />}</TableCell>
        <TableCell>{user.external !== undefined && <TypeBadge external={user.external} />}</TableCell>
        <TableCell className="whitespace-normal">
            {user.roles?.length ? (
                <div className="flex flex-wrap gap-1">
                    {user.roles.map((role) => (
                        <Badge key={role} variant="secondary">
                            {formatRole(role)}
                        </Badge>
                    ))}
                </div>
            ) : (
                <span className="text-muted-foreground">None</span>
            )}
        </TableCell>
    </TableRow>
))
